/**
 * Error Logger Utility
 * Stores errors in localStorage and provides export functionality
 * ALSO optionally sends to Google Sheet via Apps Script Web App (no backend needed)
 */

export interface ErrorLogEntry {
  timestamp: string;
  module: string;
  action: string;
  error: {
    message: string;
    stack?: string;
  };
  context: {
    userId?: string;
    userEmail?: string;
    licenseType?: string;
    userName?: string;
    url?: string;
    method?: string;
    requestBody?: any;
    additionalInfo?: Record<string, any>;
    fileName?: string;
    lineNumber?: number;
  };
}

const LOG_STORAGE_KEY = "error_logs";
const MAX_LOG_ENTRIES = 1000;
const LOG_EXPORT_FILENAME = "error_logs.json";

const SHEET_WEBAPP_URL = process.env.REACT_APP_ERROR_SHEET_WEBAPP_URL;
const SHEET_SECRET = process.env.REACT_APP_ERROR_SHEET_SECRET;

function safeJsonParse(text: string) {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * Get all stored error logs
 */
export function getErrorLogs(): ErrorLogEntry[] {
  try {
    const stored = localStorage.getItem(LOG_STORAGE_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch (error) {
    console.error("Failed to read error logs from localStorage:", error);
    return [];
  }
}

export function getErrorLogsSync(): ErrorLogEntry[] {
  return getErrorLogs();
}

/**
 * Send an error to Google Sheet (Apps Script Web App)
 * Safe: only sends minimal fields (avoid tokens/passwords!)
 */
export async function sendErrorToGoogleSheet(entry: ErrorLogEntry): Promise<void> {
  if (!SHEET_WEBAPP_URL || !SHEET_SECRET) return;

  const payload = {
    secret: SHEET_SECRET,
    timestamp: entry.timestamp,
    module: entry.module,
    action: entry.action,
    message: entry.error.message,
    stack: entry.error.stack || "",
    userId: entry.context.userId || "",
    userEmail: entry.context.userEmail || "",
    licenseType: entry.context.licenseType || "",
    userName: entry.context.userName || "",
    url: entry.context.url || "",
    method: entry.context.method || "",
    fileName: entry.context.fileName || "",
    lineNumber: entry.context.lineNumber ? String(entry.context.lineNumber) : "",
    requestBody: entry.context.requestBody
      ? JSON.stringify(entry.context.requestBody).slice(0, 8000)
      : "",
  };

  try {
    // If available, use sendBeacon (best for logging)
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      navigator.sendBeacon(SHEET_WEBAPP_URL, blob);
      return;
    }

    await fetch(SHEET_WEBAPP_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // don't crash UI
  }
}

/**
 * Fetch logs from Google Sheet
 */
export async function fetchErrorLogsFromGoogleSheet(limit = 500): Promise<ErrorLogEntry[]> {
  if (!SHEET_WEBAPP_URL || !SHEET_SECRET) return [];

  const url = `${SHEET_WEBAPP_URL}?secret=${encodeURIComponent(SHEET_SECRET)}&limit=${limit}`;

  const res = await fetch(url);
  const data = await res.json();

  if (!data?.ok) return [];

  const rows: any[] = data.rows || [];
  return rows.map((r) => ({
    timestamp: r.timestamp || "",
    module: r.module || "",
    action: r.action || "",
    error: {
      message: r.message || "",
      stack: r.stack || "",
    },
    context: {
      userId: r.userId || "",
      userEmail: r.userEmail || "",
      licenseType: r.licenseType || "",
      userName: r.userName || "",
      url: r.url || "",
      method: r.method || "",
      fileName: r.fileName || "",
      lineNumber: r.lineNumber ? Number(r.lineNumber) : undefined,
      requestBody: r.requestBody ? safeJsonParse(r.requestBody) : undefined,
    },
  }));
}

/**
 * Add an error log entry (localStorage + optional sheet)
 */
export function addErrorLog(entry: Omit<ErrorLogEntry, "timestamp">): void {
  try {
    const logs = getErrorLogs();

    const newEntry: ErrorLogEntry = {
      ...entry,
      timestamp: new Date().toISOString(),
    };

    logs.unshift(newEntry);

    if (logs.length > MAX_LOG_ENTRIES) {
      logs.splice(MAX_LOG_ENTRIES);
    }

    localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(logs));

    // optional Google Sheet logging
    sendErrorToGoogleSheet(newEntry);
  } catch (error) {
    console.error("Failed to save error log:", error);
  }
}

export function clearErrorLogs(): void {
  try {
    localStorage.removeItem(LOG_STORAGE_KEY);
  } catch (error) {
    console.error("Failed to clear error logs:", error);
  }
}

export function exportErrorLogs(): void {
  try {
    const logs = getErrorLogs();
    const dataStr = JSON.stringify(logs, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${LOG_EXPORT_FILENAME}_${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Failed to export error logs:", error);
    alert("Failed to export error logs. Please check console for details.");
  }
}

export function exportErrorLogsAsText(): void {
  try {
    const logs = getErrorLogs();
    const lines: string[] = [];

    lines.push("=".repeat(80));
    lines.push("ERROR LOGS EXPORT");
    lines.push(`Generated: ${new Date().toISOString()}`);
    lines.push(`Total Errors: ${logs.length}`);
    lines.push("=".repeat(80));
    lines.push("");

    logs.forEach((log, index) => {
      lines.push(`[${index + 1}] ${log.timestamp}`);
      lines.push(`Module: ${log.module}`);
      lines.push(`Action: ${log.action}`);
      lines.push(`Error: ${log.error.message}`);
      if (log.error.stack) {
        lines.push(`Stack Trace:`);
        lines.push(log.error.stack);
      }
      if (log.context.fileName) {
        lines.push(`File: ${log.context.fileName}${log.context.lineNumber ? `:${log.context.lineNumber}` : ""}`);
      }
      if (log.context.userId) {
        lines.push(`User ID: ${log.context.userId}`);
      }
      if (log.context.url) {
        lines.push(`URL: ${log.context.method || "GET"} ${log.context.url}`);
      }
      if (log.context.additionalInfo && Object.keys(log.context.additionalInfo).length > 0) {
        lines.push(`Additional Info: ${JSON.stringify(log.context.additionalInfo, null, 2)}`);
      }
      lines.push("-".repeat(80));
      lines.push("");
    });

    const dataStr = lines.join("\n");
    const dataBlob = new Blob([dataStr], { type: "text/plain" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `error_logs_${new Date().toISOString().split("T")[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Failed to export error logs as text:", error);
    alert("Failed to export error logs. Please check console for details.");
  }
}

export type ErrorLogStats = {
  total: number;
  byModule: Record<string, number>;
  byAction: Record<string, number>;
  recent: number;
};

/**
 * Compute stats from a given array of logs (e.g. when loaded from Google Sheet)
 */
export function getErrorLogStatsFromLogs(logs: ErrorLogEntry[]): ErrorLogStats {
  const now = Date.now();
  const oneDayAgo = now - 24 * 60 * 60 * 1000;

  const byModule: Record<string, number> = {};
  const byAction: Record<string, number> = {};
  let recent = 0;

  logs.forEach((log) => {
    byModule[log.module] = (byModule[log.module] || 0) + 1;
    byAction[log.action] = (byAction[log.action] || 0) + 1;

    const logTime = new Date(log.timestamp).getTime();
    if (logTime > oneDayAgo) recent++;
  });

  return { total: logs.length, byModule, byAction, recent };
}

/**
 * Get error log statistics (from localStorage)
 */
export function getErrorLogStats(): ErrorLogStats {
  return getErrorLogStatsFromLogs(getErrorLogs());
}

/**
 * Get errors filtered by module
 */
export function getErrorsByModule(module: string): ErrorLogEntry[] {
  return getErrorLogs().filter((log) => log.module === module);
}

/**
 * Get errors filtered by date range
 */
export function getErrorsByDateRange(startDate: Date, endDate: Date): ErrorLogEntry[] {
  const start = startDate.getTime();
  const end = endDate.getTime();

  return getErrorLogs().filter((log) => {
    const logTime = new Date(log.timestamp).getTime();
    return logTime >= start && logTime <= end;
  });
}
